import React, { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import dayjs from 'dayjs'
import { BulletinDetailsFragment } from 'shared/schema'
import { useSiteContext } from 'src/components/AppWrapper'
import {
	HeroWrapper,
	HeroViewport,
	HeroSlides,
	HeroSlide,
	HeroOverlayContainer,
	HeroTitleBox,
	HeroDepartment,
	HeroTitle,
	HeroMeta,
	HeroArrow,
	HeroBars,
	HeroBar,
	MoreNewsLink,
	HeroTitleBoxLink
} from './styled'
import CarouselArrow from 'url:./assets/carousel-arrow.svg'
import NewsArrow from 'url:./assets/news-arrow.svg'

const AUTOPLAY_DELAY_MS = 6000

function HeroCarousel({ bulletins }: { bulletins: BulletinDetailsFragment[] }) {
	const { siteMeta } = useSiteContext()
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
		Autoplay({ delay: AUTOPLAY_DELAY_MS, stopOnMouseEnter: true, stopOnInteraction: false })
	])
	const [selectedIndex, setSelectedIndex] = useState(0)

	useEffect(() => {
		if (!emblaApi) return
		const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap())
		onSelect()
		emblaApi.on('select', onSelect)
		emblaApi.on('reInit', onSelect)
		return () => {
			emblaApi.off('select', onSelect)
			emblaApi.off('reInit', onSelect)
		}
	}, [emblaApi])

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])
	const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi])

	const departmentTitle = (bulletin: BulletinDetailsFragment) => {
		const departmentId = bulletin.bulletinDepartments?.[0]?.departmentId
		return siteMeta.departments.find((d) => d.id === departmentId)?.title
	}

	return (
		<HeroWrapper>
			<HeroViewport ref={emblaRef}>
				<HeroSlides>
					{bulletins.map((b) => {
						const department = departmentTitle(b)
						const meta = [dayjs(b.createdAt).format('MMMM D, YYYY')]
						if (b.author?.firstName) {
							meta.push(`By ${b.author.firstName} ${b.author.lastName}`)
						}
						return (
							<HeroSlide key={`hero-slide-${b.id}`} $imageUrl={b.leadWebUrl}>
								<HeroOverlayContainer>
									<HeroTitleBox>
										{department && <HeroDepartment>{department}</HeroDepartment>}
										<HeroTitle>{b.title}</HeroTitle>
										<HeroMeta>{meta.join(' • ')}</HeroMeta>
										<HeroTitleBoxLink to={`/news/${b.id}`}>Read more</HeroTitleBoxLink>
									</HeroTitleBox>
								</HeroOverlayContainer>
							</HeroSlide>
						)
					})}
				</HeroSlides>
			</HeroViewport>
			<HeroOverlayContainer $controls>
				<HeroArrow $side="left" onClick={scrollPrev} aria-label="Previous story">
					<img src={CarouselArrow} alt="" />
				</HeroArrow>
				<HeroBars>
					{bulletins.map((b, index) => (
						<HeroBar
							key={`hero-bar-${b.id}`}
							$active={index === selectedIndex}
							onClick={() => scrollTo(index)}
							aria-label={`Go to story ${index + 1}`}
						/>
					))}
				</HeroBars>
				<HeroArrow $side="right" onClick={scrollNext} aria-label="Next story">
					<img src={CarouselArrow} alt="" />
				</HeroArrow>
			</HeroOverlayContainer>
			<MoreNewsLink to="/news">
				More news
				<img src={NewsArrow} alt="" />
			</MoreNewsLink>
		</HeroWrapper>
	)
}

export default HeroCarousel
